const PARALLAX_MAX_AMOUNT = 100;
const PARALLAX_DEFAULT_AMOUNT = 100;
const PARALLAX_MIN_ZOOM = 100;
const PARALLAX_MAX_ZOOM = 300;
const PARALLAX_BREAKPOINTS = 3;

/**
 * Class name the ContentBlock gets when parallax is enabled. Shared, because
 * ancestors need it to opt out of being a scroll container -- see
 * `BaseStyledStyling.ParallaxSafeOverflow`.
 */
export const PARALLAX_MODIFIER = 'contentBlock--parallax';

const PARALLAX_OFF_VALUES = ['off', 'false', '0', 'no'];
const PARALLAX_ON_VALUES = ['on', 'true', 'yes'];

export type ParallaxTiming = {
  /** `animation-range`, with both ends always written out. */
  range: string;
  /** The `animation-timing-function` that suits that range. */
  easing: string;
  /**
   * How far past the crop the pan may run at its end, in percent. Beyond the crop
   * there is no image left, so this exposes the picture's background at the block's
   * top edge -- only safe where that edge is off screen by the time it happens.
   */
  overshoot?: number;
};

/**
 * The tuned default. Pairing `entry p%` with `exit (100 - p)%` keeps the range
 * symmetric about the block being centred, and spans one viewport plus whatever of
 * the block's height is left over -- so a smaller p is a longer, calmer range. Also
 * the CSS-side fallback, for when the class is toggled without the custom properties.
 */
export const PARALLAX_DEFAULT_TIMING: ParallaxTiming = {
  range: 'entry 15% exit calc(85% - var(--top-nav-height))',
  easing: 'ease-in-out',
};

/**
 * Which slice of the block's journey through the viewport the pan is spread over, and
 * the easing that suits it. Both ends of every range are written out: a lone range
 * name leans on a subtle shorthand rule for the omitted end, and that end is the one
 * that decides what you see.
 *
 * Named for where the block sits on the page, which is the choice an author actually
 * makes; the CSS ranges they map to are named for the mechanism. Only the positions
 * that need their own range are here. A block at either end of the page cannot reach
 * one side of its range -- there is no scroll above the first block and none below the
 * last -- so its pan would otherwise sit part-spent at rest:
 *
 * - `first` starts half a block early, so the pan is already underway on load and
 *   moves from the first pixel of scroll, decelerating as the block leaves. How early
 *   to start is a trade: everything before scroll 0 is unreachable, and `ease-out`
 *   front-loads the travel into exactly that part. Measured on the /en_de hero (top
 *   96, height 539, crop 171px): -85% loads at 33% of the crop and shows 57px, -50%
 *   loads at 50% and shows 86px, -25% shows 130px but loads three quarters in, well
 *   away from the composed framing. Offsets are in block heights, so this holds at any
 *   viewport height -- only the block's distance from the top of the page moves it.
 *   It also overshoots the crop by 25% at the end. Past the crop there is no image
 *   left, so that exposes the picture's background at the block's top edge -- but by
 *   then the edge is far above the viewport. Measured on the /en_de hero: the crop
 *   runs out around scrollY 190 with the top edge already ~100px off screen, and the
 *   gap peaks 97px above the fold. It buys travel twice over, because stretching the
 *   value span also re-maps the same scroll onto a lower starting point: 128px of
 *   travel from a 50% first paint becomes 160px from 38%, nearer the composed shot.
 *   Only safe for a block whose top edge has left the viewport by the time the crop
 *   runs out, which is why it lives here and not on the share.
 * - `last` mirrors it for a block at the bottom, which can never leave the top of the
 *   viewport. It starts part-spent as the block appears and comes to rest around three
 *   fifths through at the furthest the page can scroll, accelerating in.
 *
 * Every range ends on the same anchor, and that is deliberate: two blocks whose pans
 * finish at the same point in their journey converge as they approach it, so a `first`
 * block and a `default` one below it agree to within a few pixels while either sits at
 * the top of the viewport. Changing one end anchor and not the others pulls them apart.
 *
 * A Map, not an object: the authored value is a lookup key, and a plain object would
 * answer for inherited names like `constructor` with a function.
 */
const PARALLAX_RANGES = new Map<string, ParallaxTiming>([
  ['default', PARALLAX_DEFAULT_TIMING],
  [
    'first',
    {
      range: 'exit -50% exit calc(85% - var(--top-nav-height))',
      easing: 'ease-out',
      overshoot: 25,
    },
  ],
  ['last', { range: 'entry -50% entry 200%', easing: 'ease-in' }],
]);

type ParallaxAttributes = {
  /**
   * `data-parallax`: one value for every breakpoint, or a comma separated list per
   * breakpoint -- `on`, `off,on`, `on,off,60`.
   */
  amount?: string;
  /**
   * `data-parallax-scale`: how far to zoom in, as a percentage, per breakpoint the
   * same way -- `130`, `100,130,150`. Manufactures a crop for sources that have no
   * spare height of their own.
   */
  scale?: string;
  /**
   * `data-parallax-range`: where the block sits on the page -- `first`, `last`, or
   * `default` for anything in between -- per breakpoint the same way. Anything else
   * falls back to `default`.
   */
  range?: string;
};

export type ParallaxBreakpoint = {
  /** `object-position` Y at the start of the scroll range, in percent. */
  from: number;
  /** ...and at the end of it. */
  to: number;
  /** How far to zoom in, as a percentage; 100 leaves the box alone. */
  zoom: number;
  /** `animation-range` and its easing, from the fixed set above. */
  range: string;
  easing: string;
};

export type ParallaxConfig = {
  enabled: boolean;
  /** Settings per breakpoint, matching the three <source> images. Off where unset. */
  mobile?: ParallaxBreakpoint;
  tablet?: ParallaxBreakpoint;
  desktop?: ParallaxBreakpoint;
};

const round = (value: number): number => Number(value.toFixed(3));

const getShare = (value?: string): number | undefined => {
  const normalized = value?.trim().toLowerCase();

  if (!normalized || PARALLAX_OFF_VALUES.includes(normalized)) {
    return undefined;
  }

  if (PARALLAX_ON_VALUES.includes(normalized)) {
    return PARALLAX_DEFAULT_AMOUNT;
  }

  const share = Number.parseFloat(normalized);

  if (Number.isNaN(share) || share <= 0) {
    return undefined;
  }

  return Math.min(share, PARALLAX_MAX_AMOUNT);
};

/**
 * A wider box is what creates vertical crop for `object-position` to pan through:
 * `object-fit: cover` only leaves spare height when the box's aspect ratio is wider
 * than the source's. Widening the box therefore zooms the image and buys travel;
 * making it taller would spend the overflow sideways instead and pan nothing.
 */
const getZoom = (value?: string): number => {
  const zoom = Number.parseFloat(value?.trim() ?? '');

  if (Number.isNaN(zoom) || zoom <= PARALLAX_MIN_ZOOM) {
    return PARALLAX_MIN_ZOOM;
  }

  return Math.min(zoom, PARALLAX_MAX_ZOOM);
};

/**
 * Spreads the authored values over mobile / tablet / desktop the way the rest of the
 * responsive content attributes do: one value covers every breakpoint, two make the
 * second cover everything above mobile, three map one to one. Extras are ignored.
 */
const perBreakpoint = (value = ''): string[] => {
  const values = value.split(',').slice(0, PARALLAX_BREAKPOINTS);

  if (values.length === 1) {
    return [values[0], values[0], values[0]];
  }

  if (values.length === 2) {
    return [values[0], values[1], values[1]];
  }

  return values;
};

const getTiming = (value?: string): ParallaxTiming =>
  PARALLAX_RANGES.get(value?.trim().toLowerCase() ?? '') ?? PARALLAX_DEFAULT_TIMING;

const getBreakpoint = (
  amount?: string,
  scale?: string,
  range?: string
): ParallaxBreakpoint | undefined => {
  const share = getShare(amount);

  if (share === undefined) {
    return undefined;
  }

  const zoom = getZoom(scale);
  const timing = getTiming(range);

  return {
    from: round(50 + share / 2),
    to: round(50 - share / 2 - (timing.overshoot ?? 0)),
    zoom,
    range: timing.range,
    easing: timing.easing,
  };
};

/**
 * Parses the `data-parallax`, `data-parallax-scale` and `data-parallax-range` content
 * attributes into what the CSS animation needs, per breakpoint.
 *
 * The image box is never moved: the `object-fit: cover` crop slides through the source
 * instead, which is why there is no geometry to keep in sync with the frame. The travel
 * is bounded by the spare height that crop has, which comes from either
 *
 * - a source taller than the frame, costing nothing; or
 * - `data-parallax-scale`, which widens the box by that percentage and so zooms the
 *   image, manufacturing `scale - 100`% of the frame height in spare height for a
 *   source that fits the frame exactly. A zoom always softens the image a little.
 *
 * `amount` is what share of the available crop to spend: `100` (or `on`, the default)
 * uses all of it, `50` the middle half, `off` disables that breakpoint. The endpoints
 * stay centred on `50%`, so the crop is spent symmetrically and can never overshoot
 * into an exposed edge.
 *
 * `range` picks which slice of the block's journey to spend that travel over; see
 * PARALLAX_RANGES for what each one is good for.
 */
export const getParallaxConfig = ({ amount, scale, range }: ParallaxAttributes): ParallaxConfig => {
  if (!amount?.trim()) {
    return { enabled: false };
  }

  const amounts = perBreakpoint(amount);
  const scales = perBreakpoint(scale);
  const ranges = perBreakpoint(range);
  const [mobile, tablet, desktop] = amounts.map((value, i) =>
    getBreakpoint(value, scales[i], ranges[i])
  );

  return {
    enabled: Boolean(mobile || tablet || desktop),
    mobile,
    tablet,
    desktop,
  };
};
